import React from 'react';
import { Phone, Mail, MapPin, Clock, Check } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prairie-grains/artifacts/4rxtb62e_Robert%20-Good%20Grains.jpg";

const FlyerPage = () => {
  return (
    <div className="min-h-screen bg-gray-200 py-4 print:py-0 print:bg-white flex items-center justify-center">
      {/* Flyer Container - 8.5 x 11 inches */}
      <div 
        className="bg-[#FAF6F0] w-[8.5in] shadow-2xl print:shadow-none overflow-hidden"
        style={{ height: '11in' }}
      >
        {/* Header Section */}
        <div className="relative bg-[#2D5016] text-white px-6 py-6 overflow-hidden">
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-[#D4A84B]" />
          
          {/* Logo and Title */}
          <div className="relative z-10 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <img 
                src={LOGO_URL}
                alt="Good Grains Logo"
                className="w-24 h-24 rounded-full object-cover shadow-xl border-4 border-[#D4A84B] ring-2 ring-white/30"
                style={{ imageRendering: 'crisp-edges' }}
              />
            </div>
            <h1 className="font-serif text-4xl font-bold mb-1">Good Grains</h1>
            <p className="text-[#D4A84B] text-base font-medium tracking-wide uppercase">
              Organic & Non-GMO Animal Feed
            </p>
            <p className="text-white/80 text-sm">Kalispell, Montana</p>
          </div>
        </div>

        {/* Tagline Banner */}
        <div className="bg-[#D4A84B] text-[#3B2F1E] py-2.5 px-6 text-center">
          <p className="font-serif text-xl font-semibold italic">
            "Healthy Feeds Your Animals Will Love"
          </p>
        </div>

        {/* Main Content */}
        <div className="px-6 py-5">
          {/* Introduction */}
          <div className="text-center mb-5">
            <p className="text-[#3B2F1E] text-sm leading-relaxed max-w-2xl mx-auto">
              At Good Grains, we believe healthy animals start with honest feed. 
              From backyard flocks to full-size farms, we provide Non-GMO and Organic 
              feed to help your animals grow strong and thrive.
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-2 gap-6 mb-5">
            {/* Left Column - What We Offer */}
            <div>
              <h2 className="font-serif text-xl font-bold text-[#2D5016] mb-3 border-b-2 border-[#D4A84B] pb-1">
                What We Offer
              </h2>
              <ul className="space-y-1.5">
                {[
                  'Poultry Feed (Layer, Broiler, Chick Grower)',
                  'Dairy & Livestock Feed',
                  'Swine Feed',
                  'Supplements & Alfalfa Pellets',
                  'Bulk & 50 lb Bag Options',
                  '1,000 lb Totes Available'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#2D5016] mt-0.5 flex-shrink-0" />
                    <span className="text-[#3B2F1E] text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Animals We Serve */}
              <h3 className="font-serif text-base font-bold text-[#2D5016] mt-4 mb-2">
                Feed For All Your Animals
              </h3>
              <p className="text-[#3B2F1E] text-xs">
                Chickens • Goats • Cattle • Pigs • Sheep • Horses • Rabbits & More
              </p>
            </div>

            {/* Right Column - Why Choose Us */}
            <div>
              <h2 className="font-serif text-xl font-bold text-[#2D5016] mb-3 border-b-2 border-[#D4A84B] pb-1">
                Why Choose Us?
              </h2>
              <ul className="space-y-1.5">
                {[
                  'Quality Non-GMO & Organic Feed',
                  'Locally Owned & Operated',
                  'Personal Service & Expert Guidance',
                  'Local Delivery Drop-offs',
                  'Easy Pickup at Our Location',
                  'Competitive Pricing'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#D4A84B] mt-0.5 flex-shrink-0" />
                    <span className="text-[#3B2F1E] text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Testimonial */}
              <div className="mt-4 bg-white/60 rounded-lg p-3 border-l-4 border-[#2D5016]">
                <p className="text-[#3B2F1E] italic text-xs leading-relaxed">
                  "Our livestock have done really well on the feed. We're grateful 
                  to have a local feed supplier we trust!"
                </p>
                <p className="text-[#2D5016] font-semibold text-xs mt-1">
                  — Mark L., Whitefish, MT
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Highlights */}
          <div className="bg-[#2D5016]/10 rounded-xl p-4 mb-5">
            <h2 className="font-serif text-xl font-bold text-[#2D5016] text-center mb-3">
              Quality Feed at Fair Prices
            </h2>
            <div className="grid grid-cols-4 gap-3 text-center">
              {[
                { name: 'Layer Feed', price: '$24-26' },
                { name: 'Chick Grower', price: '$26' },
                { name: 'All Stock', price: '$24' },
                { name: 'Alfalfa Pellets', price: '$21' }
              ].map((item) => (
                <div key={item.name} className="bg-white rounded-lg p-2.5 shadow-sm">
                  <p className="text-[#3B2F1E] font-medium text-xs">{item.name}</p>
                  <p className="text-[#2D5016] font-bold text-lg">{item.price}</p>
                  <p className="text-[#3B2F1E]/60 text-[10px]">per 50 lb bag</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-[#3B2F1E] text-white rounded-xl p-4">
            <h2 className="font-serif text-lg font-bold text-center mb-3 text-[#D4A84B]">
              Contact Us Today!
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[9px] uppercase tracking-wide">Phone</p>
                    <p className="font-bold text-sm">406-607-2302</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[9px] uppercase tracking-wide">Email</p>
                    <p className="font-bold text-xs">Robert@goodgrainsmt.com</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[9px] uppercase tracking-wide">Location</p>
                    <p className="font-bold text-xs">1185 Majestic View Lane, Kalispell, MT 59901</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[9px] uppercase tracking-wide">Hours</p>
                    <p className="font-bold text-xs">Mon-Sat: 9AM - 6PM | Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Matching Header Style */}
        <div className="relative bg-[#2D5016] text-white px-6 py-3 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#D4A84B]" />

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src={LOGO_URL}
                alt="Good Grains Logo"
                className="w-10 h-10 rounded-full object-cover border-2 border-[#D4A84B] shadow-md"
                style={{ imageRendering: 'crisp-edges' }}
              />
              <span className="font-serif text-sm font-bold">Good Grains</span>
            </div>
            <div className="text-center">
              <p className="text-[#D4A84B] text-xs font-semibold">
                Join the Good Grains Community!
              </p>
              <p className="text-white/70 text-[10px]">
                Family-owned • Faith-driven • Community-focused
              </p>
            </div>
            <p className="text-white/60 text-[10px]">
              Facebook • Instagram • TikTok
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: 8.5in 11in;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:py-0 {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FlyerPage;
